import { useState } from "react";
import { upsertSpending, deleteSpending, newId, type CanonicalStore, type Car, type Spending } from "../../core/index.ts";
import { Modal, NumberField, SelectField, DateField, TextField, FormActions, parseNum, numToStr } from "./Modal.tsx";
import { PhotoField, DocField } from "./Attachments.tsx";
import { epochToDateInput, dateInputToEpoch } from "../format.ts";

const CATS = ["Insurance", "Registration/Tax", "Parking", "Toll", "Repair", "General Maintenance", "Car Wash", "Tires", "Other"];

export function SpendingForm({ store, car, editing, onSave, onClose }: {
  store: CanonicalStore; car: Car; editing?: Spending; onSave: (s: CanonicalStore) => void; onClose: () => void;
}) {
  const [amount, setAmount] = useState(editing ? numToStr(editing.amount) : "");
  const [category, setCategory] = useState(editing?.category ?? "Other");
  const [dateStr, setDate] = useState(epochToDateInput(editing?.date ?? null));
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [photos, setPhotos] = useState<string[]>(editing?.photoFilenames ?? []);
  const [docs, setDocs] = useState<string[]>(editing?.documentFilenames ?? []);
  const currency = store.profile.displayCurrency;
  // "Other" is a catch-all, so a note is required to keep the entry meaningful.
  const noteRequired = category === "Other";

  return (
    <Modal title={editing ? "Edit expense" : "New expense"} onClose={onClose}>
      <NumberField label={`Amount (${currency})`} value={amount} onChange={setAmount} />
      <SelectField label="Category" value={category} onChange={setCategory} options={CATS.includes(category) ? CATS : [category, ...CATS]} />
      <DateField label="Date" value={dateStr} onChange={setDate} />
      <TextField label={noteRequired ? "Notes (required)" : "Notes (optional)"} value={notes} onChange={setNotes} />
      <PhotoField label="Photos (receipt…)" filenames={photos} onChange={setPhotos} />
      <DocField label="Documents" filenames={docs} onChange={setDocs} />
      <FormActions
        canSave={parseNum(amount) > 0 && (!noteRequired || notes.trim().length > 0)}
        onSave={() => {
          const s: Spending = {
            id: editing?.id ?? newId(), carId: car.id, amount: parseNum(amount), category,
            date: dateInputToEpoch(dateStr), notes: notes.trim() || null, currency, fuelLogId: null,
            photoFilenames: photos, documentFilenames: docs,
          };
          onSave(upsertSpending(store, s)); onClose();
        }}
        onDelete={editing ? () => { onSave(deleteSpending(store, editing.id)); onClose(); } : undefined}
      />
    </Modal>
  );
}
